import { type SalesMetricV2 } from "./types";

export function makePinterestCheckoutConversions(): SalesMetricV2 {
  const id = "pinterestCheckoutConversions";
  const name = "Pinterest Checkout Conversions";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of checkout events that resulted from interactions with your ad. This metric " +
      "may include conversions that are modeled.",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestCheckoutOrderValue(): SalesMetricV2 {
  const id = "pinterestCheckoutOrderValue";
  const name = "Pinterest Checkout Order Value";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total value of checkout events resulting from interactions with your ad. This metric " +
      "may include conversions that are modeled",
    category: "pinterest",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestAddToCartRate(): SalesMetricV2 {
  const id = "pinterestAddToCartRate";
  const name = "Pinterest Add To Cart Rate";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of conversions per the number of total ad impressions that can be tracked to " +
      "a conversion during the same time period.",
    category: "pinterest",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidImpressions(): SalesMetricV2 {
  const id = "pinterestPaidImpressions";
  const name = "Pinterest Paid Impressions";

  return {
    id,
    name,
    fullName: name,
    description: "The number of times your ad was seen",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidEngagements(): SalesMetricV2 {
  const id = "pinterestPaidEngagements";
  const name = "Pinterest Paid Engagements";

  return {
    id,
    name,
    fullName: name,
    description: "Paid actions taken as a result of your ads",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidPinClicks(): SalesMetricV2 {
  const id = "pinterestPaidPinClicks";
  const name = "Pinterest Pin Clicks";

  return {
    id,
    name,
    fullName: name,
    description:
      "The total number of clicks on your Pin to content on or off of Pinterest",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidSaveRate(): SalesMetricV2 {
  const id = "pinterestPaidSaveRate";
  const name = "Pinterest Paid Save Rate";

  return {
    id,
    name,
    fullName: name,
    description: "Total number of saves for your ad / Impressions",
    category: "pinterest",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidSaves(): SalesMetricV2 {
  const id = "pinterestPaidSaves";
  const name = "Pinterest Paid Saves";

  return {
    id,
    name,
    fullName: name,
    description: "Total number of saves for your ad",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoViews(): SalesMetricV2 {
  const id = "pinterestPaidVideoViews";
  const name = "Pinterest Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played continuously for at least 2 seconds " +
      "while 50% visible (paid and earned) - The amount of views for at least 2 seconds " +
      "with 50% of video in view.",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaid3SecVideoViews(): SalesMetricV2 {
  const id = "pinterestPaid3SecVideoViews";
  const name = "Pinterest Paid 3 second Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played continuously for at least 3 seconds " +
      "while 50% visible (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoPlayedAt50(): SalesMetricV2 {
  const id = "pinterestPaidVideoPlayedAt50";
  const name = "Pinterest Paid Video Played At 50%";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played 50% of its length, including views that " +
      "skipped ahead to this point (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoPlayedAt95(): SalesMetricV2 {
  const id = "pinterestPaidVideoPlayedAt95";
  const name = "Pinterest Paid Video Played At 95%";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played 95% of its length, including views that skipped " +
      "ahead to this point (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoPlayedAt75(): SalesMetricV2 {
  const id = "pinterestPaidVideoPlayedAt75";
  const name = "Pinterest Paid Video Played At 75%";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played 75% of its length, including views that skipped " +
      "ahead to this point (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoPlayedAt100(): SalesMetricV2 {
  const id = "pinterestPaidVideoPlayedAt100";
  const name = "Pinterest Paid Video Played At 100%";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played 100% of its length, including views that skipped " +
      "ahead to this point (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoPlayedAt25(): SalesMetricV2 {
  const id = "pinterestPaidVideoPlayedAt25";
  const name = "Pinterest Paid Video Played At 25%";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad played 25% of its length, including views that skipped " +
      "ahead to this point (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidVideoStarts(): SalesMetricV2 {
  const id = "pinterestPaidVideoStarts";
  const name = "Pinterest Paid Video Starts";

  return {
    id,
    name,
    fullName: name,
    description:
      "Total number of times your video ad started playing (paid and earned)",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestPaidIdeaPinProductTagVisit(): SalesMetricV2 {
  const id = "pinterestPaidIdeaPinProductTagVisit";
  const name = "Pinterest Paid Idea Pin Product Tag Visit";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times someone taps on a product tagged within an Idea Pin you promoted",
    category: "pinterest",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

// pinterestSpendInAccountCurrency only reports in UTC. We don't shift it to client's
// timezone so we are able to match Pinterest ads manager since it always reports in UTC.
// Because of this, there will be a small discrepancy
// between Northbeam spend and Pinterest reported spend.
export function makePinterestROAS(): SalesMetricV2 {
  const id = "pinterestROAS";
  const name = "Pinterest ROAS";

  return {
    id,
    name,
    fullName: name,
    description:
      "Pinterest reported ROAS, this is calculated by taking the total revenue reported by " +
      "Pinterest and dividing by spend",
    category: "pinterest",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makePinterestCAC(): SalesMetricV2 {
  const id = "pinterestCAC";
  const name = "Pinterest CAC";

  return {
    id,
    name,
    fullName: name,
    description:
      "Calculated by taking the total spend reported by Pinterest and dividing by total number " +
      "of purchases",
    category: "pinterest",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
